// Zones of one site (CR_20260914 §4.3): add, rename, archive / restore. Managers only (RLS enforces it too).
// No delete: archiving takes the zone off its assets and history keeps the name (decision Z5).
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteBoard } from '../data/queries';
import { type SiteZone, useSaveZone, useSetZoneActive, useZones } from '../data/zones';
import { zoneCounts } from '../lib/zones';
import { ZoneSignsDialog } from './LabelsDialog';
import { Loading, LoadError } from './Page';

const NAME_MAX = 40;
const NOTE_MAX = 200;

function errorText(err: unknown, t: (k: string) => string): string {
  const e = err as { code?: string; message?: string } | null;
  if (e?.code === '23505') return t('zones.name_taken');
  return `${t('common.error_generic')} ${e?.message ?? ''}`.trim();
}

/** Name + note fields, used for adding and for renaming. */
function ZoneForm({
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: SiteZone;
  submitLabel: string;
  busy: boolean;
  onSubmit(name: string, note: string): void;
  onCancel?(): void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const ok = name.trim().length > 0 && name.trim().length <= NAME_MAX && note.length <= NOTE_MAX;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) onSubmit(name, note);
  };
  return (
    <Box component="form" onSubmit={submit}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label={t('zones.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
          autoFocus={!!initial}
          required
        />
        <TextField
          size="small"
          label={`${t('zones.note')} (${t('common.optional')})`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          sx={{ flexGrow: 1 }}
        />
        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" size="small" disabled={!ok || busy}>
            {submitLabel}
          </Button>
          {onCancel ? (
            <Button size="small" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export function ZonesDialog({
  siteId,
  siteName,
  companyId,
  onClose,
}: {
  siteId: string;
  siteName: string;
  companyId: string;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const zones = useZones(siteId);
  const board = useSiteBoard(siteId);
  const save = useSaveZone();
  const setActive = useSetZoneActive();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(0); // bump to reset the add form after a save
  const [archiving, setArchiving] = useState<SiteZone | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [printingSigns, setPrintingSigns] = useState(false);

  const counts = useMemo(() => zoneCounts(board.data ?? [], siteId).byZone, [board.data, siteId]);
  const active = (zones.data ?? []).filter((z) => z.is_active);
  const archived = (zones.data ?? []).filter((z) => !z.is_active);
  const error = save.error ?? setActive.error;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('zones.title', { site: siteName })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('zones.intro')}
        </Typography>
        {zones.isLoading ? (
          <Loading />
        ) : zones.error ? (
          <LoadError error={zones.error} />
        ) : (
          <>
            {active.length === 0 ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('zones.empty')}
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <Table size="small" data-testid="zones-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('zones.name')}</TableCell>
                      <TableCell>{t('zones.note')}</TableCell>
                      <TableCell align="right">{t('zones.assets')}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {active.map((z) =>
                      editing === z.id ? (
                        <TableRow key={z.id}>
                          <TableCell colSpan={4}>
                            <ZoneForm
                              initial={z}
                              submitLabel={t('common.save')}
                              busy={save.isPending}
                              onCancel={() => setEditing(null)}
                              onSubmit={(name, note) =>
                                save.mutate(
                                  { id: z.id, companyId, siteId, name, note },
                                  { onSuccess: () => setEditing(null) },
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={z.id}>
                          <TableCell sx={{ fontWeight: 500 }}>{z.name}</TableCell>
                          <TableCell>{z.note ?? ''}</TableCell>
                          <TableCell align="right">{counts.get(z.id) ?? 0}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button size="small" onClick={() => setEditing(z.id)}>
                              {t('zones.rename')}
                            </Button>
                            <Button size="small" color="warning" onClick={() => setArchiving(z)}>
                              {t('zones.archive')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
            <ZoneForm
              key={adding}
              submitLabel={t('zones.add')}
              busy={save.isPending}
              onSubmit={(name, note) =>
                save.mutate(
                  { companyId, siteId, name, note },
                  { onSuccess: () => setAdding((n) => n + 1) },
                )
              }
            />
            {archived.length ? (
              <Box sx={{ mt: 2 }}>
                <Link component="button" type="button" onClick={() => setShowArchived((v) => !v)}>
                  {t('zones.archived', { count: archived.length })}
                </Link>
                <Collapse in={showArchived}>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {archived.map((z) => (
                      <Stack key={z.id} direction="row" spacing={1} alignItems="center">
                        <Typography color="text.secondary">{z.name}</Typography>
                        <Button
                          size="small"
                          disabled={setActive.isPending}
                          onClick={() => setActive.mutate({ id: z.id, active: true })}
                        >
                          {t('zones.restore')}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            ) : null}
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorText(error, t)}
              </Alert>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Link
          component="button"
          type="button"
          disabled={!active.length}
          onClick={() => setPrintingSigns(true)}
          sx={{ ml: 1 }}
        >
          {t('zones.print_signs')}
        </Link>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
      {archiving ? (
        <Dialog open onClose={() => setArchiving(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('zones.archive_title', { zone: archiving.name })}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('zones.archive_body', { count: counts.get(archiving.id) ?? 0 })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiving(null)}>{t('common.cancel')}</Button>
            <Button
              color="warning"
              variant="contained"
              disabled={setActive.isPending}
              onClick={() =>
                setActive.mutate(
                  { id: archiving.id, active: false },
                  { onSettled: () => setArchiving(null) },
                )
              }
            >
              {t('zones.archive')}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {printingSigns ? (
        <ZoneSignsDialog
          siteId={siteId}
          siteName={siteName}
          onClose={() => setPrintingSigns(false)}
        />
      ) : null}
    </Dialog>
  );
}
